"use client";

import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import SectionHeading from "@/src/components/common/broadsheet/section-heading";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairlineTop,
} from "@/src/components/common/broadsheet/tokens";
import {
  byTitle,
  coverSrc,
  groupByCategory,
  journalBlurb,
  journalHref,
  journalMetrics,
  pickFeaturedIds,
  spineForCategory,
  spineForJournal,
} from "@/src/components/main-site/home/journals-section/journal-helpers";

const FeaturedJournal = ({ journal }: { journal: any }) => {
  const src = coverSrc(journal);
  const metrics = journalMetrics(journal);

  return (
    <Box sx={{ display: "flex", gap: 0.75, pt: 0.75, ...hairlineTop }}>
      <Box
        sx={{
          flex: "none",
          width: "4.5rem",
          aspectRatio: "3 / 4",
          position: "relative",
          backgroundColor: "custom.newsprintRaised",
          outline: "1px solid",
          outlineColor: "custom.ruleHairline",
          outlineOffset: "-1px",
          overflow: "hidden",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt=""
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : null}
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            insetBlock: 0,
            left: 0,
            width: "4px",
            backgroundColor: spineForJournal(journal),
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h3"
          sx={{
            m: 0,
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: "1.0625rem",
            lineHeight: 1.2,
            "& a": {
              color: "custom.ink",
              textDecoration: "none",
              "&:hover": {
                color: "custom.link",
                textDecoration: "underline",
                textUnderlineOffset: "0.15em",
              },
            },
          }}
        >
          <NextLink href={journalHref(journal)}>{journal.title}</NextLink>
        </Typography>
        <MetaLine
          sx={{ mt: 0.125 }}
          items={[
            metrics.field,
            metrics.jif ? `JIF ${metrics.jif}` : null,
            metrics.citeScore ? `CiteScore ${metrics.citeScore}` : null,
          ]}
        />
        <Typography
          component="p"
          sx={{
            m: 0,
            mt: 0.25,
            fontFamily: FONT_TEXT,
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: "custom.inkMuted",
          }}
        >
          {journalBlurb(journal, 130)}
        </Typography>
      </Box>
    </Box>
  );
};

export default function JournalsSection({
  journals: serverJournals,
  featuredIds: serverFeaturedIds,
}: {
  journals?: any[];
  featuredIds?: number[];
} = {}) {
  const [journals, setJournals] = useState<any[]>(serverJournals ?? []);
  const [clientFeaturedIds, setClientFeaturedIds] = useState<number[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (serverJournals) return;

    const fetchJournals = async () => {
      try {
        const response = await fetch("/api/journals?productionStatus=true");
        const data = await response.json();
        if (data.success) {
          const list = data.data || [];
          setJournals(list);
          setClientFeaturedIds(pickFeaturedIds(list));
        } else {
          setError("Failed to load journals");
        }
      } catch (err) {
        setError("Failed to load journals");
        console.error("Error fetching journals:", err);
      }
    };

    fetchJournals();
  }, [serverJournals]);

  const featuredIds = serverFeaturedIds ?? clientFeaturedIds ?? [];

  const featured = useMemo(
    () =>
      featuredIds
        .map((id) => journals.find((journal) => journal.id === id))
        .filter(Boolean),
    [journals, featuredIds],
  );

  const groups = useMemo(() => groupByCategory(journals), [journals]);

  if (error) {
    return (
      <Box component="section" id="journals">
        <Typography component="p" sx={{ py: 1, color: "custom.inkMuted" }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="section" id="journals" aria-labelledby="journals-heading">
      <SectionHeading
        title="The journals"
        titleId="journals-heading"
        component="h2"
        deck={`${journals.length} peer-reviewed, open-access titles across ${groups.length} fields.`}
        action={{
          label: `Browse all ${journals.length}`,
          href: "/journal-finder",
        }}
      />

      {featured.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(3, minmax(0, 1fr))",
            },
            columnGap: 2.5,
            rowGap: 1.25,
            "& > *": { minWidth: 0 },
          }}
        >
          {featured.map((journal: any) => (
            <FeaturedJournal key={journal.id} journal={journal} />
          ))}
        </Box>
      ) : null}

      {groups.length > 0 ? (
        <Box sx={{ mt: featured.length > 0 ? 1.5 : 0.5 }}>
          <Kicker component="p" sx={{ mb: 0.5 }}>
            Every title by field
          </Kicker>
          <Box sx={{ columnCount: { xs: 1, sm: 2, lg: 3 }, columnGap: 2.5 }}>
            {groups.map(([field, list]) => (
              <Box key={field} sx={{ breakInside: "avoid", mb: 1.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.375,
                    pb: 0.1875,
                    borderBottom: "2px solid",
                    borderColor: "custom.ink",
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: "0.625rem",
                      height: "0.625rem",
                      flex: "none",
                      backgroundColor: spineForCategory(field),
                    }}
                  />
                  <Typography
                    component="h3"
                    sx={{
                      m: 0,
                      flex: 1,
                      minWidth: 0,
                      fontFamily: FONT_UI,
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "custom.ink",
                    }}
                  >
                    {field}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: FONT_UI,
                      fontSize: "0.625rem",
                      color: "custom.subtitleText",
                    }}
                  >
                    {list.length}
                  </Box>
                </Box>

                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {[...list].sort(byTitle).map((journal) => (
                    <Box
                      component="li"
                      key={`${field}-${journal.id}`}
                      sx={{ m: 0, py: 0.25, ...hairlineTop }}
                    >
                      <Box
                        component={NextLink}
                        href={journalHref(journal)}
                        sx={{
                          display: "block",
                          fontFamily: FONT_TEXT,
                          fontSize: "0.8125rem",
                          lineHeight: 1.3,
                          color: "custom.inkMuted",
                          textDecoration: "none",
                          "&:hover": {
                            color: "custom.link",
                            textDecoration: "underline",
                            textUnderlineOffset: "0.15em",
                          },
                        }}
                      >
                        {journal.title}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
